import React, { useState, useEffect } from 'react';
import { ScrollView } from 'react-native';
import { useSelector } from 'react-redux';
import { useLinkTo } from '@react-navigation/native';
import Typography from 'src/components/Text/Text';
import IconInput from 'src/components/IconInput/IconInput';
import InputField from 'src/components/input/input';
import RadioModal from 'src/components/RadioModal/RadioModal';
import NavigationButtons from '../../components/NavigationButtons';
import Toast from 'src/components/Toast/Toast';
import { useUpdateApplicationMutation } from '../../api/applicationApi';
import { RootState } from 'src/store/store';
import { URLS } from 'src/constants/urls';
import { toggleToastState } from 'src/helpers/toastHelper';
import arrowDown from 'src/assets/arrowDown-3x.png';
import { styles } from '../Policy/PolicyScreen.styles';

const yesNoOptions = [{ label: 'Yes' }, { label: 'No' }];
const ownershipTypeOptions = [{ label: 'Company Owned' }, { label: 'Leased O/O' }];

const boolToYesNo = (val?: boolean | null) => (val === true ? 'Yes' : val === false ? 'No' : '');
const yesNoToBool = (val: string) => (val === 'Yes' ? true : val === 'No' ? false : null);

const GrowthAndOwnershipScreen: React.FC = () => {
  const linkTo = useLinkTo();
  const application = useSelector((state: RootState) => state.application.application);
  const currentCompany = useSelector((state: RootState) => state.company.company);
  const accountType = useSelector((state: RootState) => state.UI.accountType);

  const [plansToChangeBusiness, setPlansToChangeBusiness] = useState(
    boolToYesNo(application?.plansToChangeBusiness),
  );
  const [plansForGrowth, setPlansForGrowth] = useState(boolToYesNo(application?.plansForGrowth));
  const [growthUnitsPerQuarter, setGrowthUnitsPerQuarter] = useState(
    application?.growthUnitsPerQuarter?.toString() || '',
  );
  const [growthOwnershipType, setGrowthOwnershipType] = useState(
    application?.growthOwnershipType || '',
  );
  const [plansToRelocate, setPlansToRelocate] = useState(boolToYesNo(application?.plansToRelocate));

  const [isPlansToChangeBusinessModalVisible, setPlansToChangeBusinessModalVisible] =
    useState(false);
  const [isPlansForGrowthModalVisible, setPlansForGrowthModalVisible] = useState(false);
  const [isGrowthOwnershipTypeModalVisible, setGrowthOwnershipTypeModalVisible] = useState(false);
  const [isPlansToRelocateModalVisible, setPlansToRelocateModalVisible] = useState(false);
  const [saveErrorToast, setSaveErrorToast] = useState(false);

  const [updateApplication] = useUpdateApplicationMutation();

  const isProducer = accountType === 'producer';

  useEffect(() => {
    if (!isProducer) {
      linkTo(URLS.APPLICATION.LOSS);
    }
  }, [isProducer, linkTo]);

  if (!isProducer) return null;

  const handleNext = async () => {
    try {
      await updateApplication({
        id: application?.id as number,
        updatedData: {
          plansToChangeBusiness: yesNoToBool(plansToChangeBusiness),
          plansForGrowth: yesNoToBool(plansForGrowth),
          growthUnitsPerQuarter:
            plansForGrowth === 'Yes' && growthUnitsPerQuarter
              ? parseInt(growthUnitsPerQuarter, 10)
              : null,
          growthOwnershipType: plansForGrowth === 'Yes' ? growthOwnershipType : undefined,
          plansToRelocate: yesNoToBool(plansToRelocate),
          company: { id: currentCompany?.id || 0 },
        },
      }).unwrap();
      linkTo(URLS.APPLICATION.LOSS);
    } catch (e) {
      console.error('BusinessProfile save error:', e);
      setSaveErrorToast(true);
      toggleToastState(setSaveErrorToast);
    }
  };

  return (
    <ScrollView contentContainerStyle={styles.container} showsVerticalScrollIndicator={false}>
      <Typography variant="h2">Growth & Business Plans</Typography>

      {/* Q5 */}
      <IconInput
        label="Do they plan to change their business? (e.g. commodities, team driving)"
        testID="bp-plans-to-change-business"
        value={plansToChangeBusiness}
        placeholder="Select an option"
        onPress={() => setPlansToChangeBusinessModalVisible(true)}
        iconSource={arrowDown}
        iconHeight={6.67}
        iconWidth={16.67}
      />
      <RadioModal
        testID="bp-plans-to-change-business-modal"
        isVisible={isPlansToChangeBusinessModalVisible}
        onSelectOption={setPlansToChangeBusiness}
        onButtonClickLeft={() => setPlansToChangeBusinessModalVisible(false)}
        onClose={() => setPlansToChangeBusinessModalVisible(false)}
        title="Do they plan to change their business?"
        options={yesNoOptions}
        selectedValue={plansToChangeBusiness}
      />

      {/* Q6 */}
      <IconInput
        label="Any plans for growth or reduction of units, or vehicles rented short-term?"
        testID="bp-plans-for-growth"
        value={plansForGrowth}
        placeholder="Select an option"
        onPress={() => setPlansForGrowthModalVisible(true)}
        iconSource={arrowDown}
        iconHeight={6.67}
        iconWidth={16.67}
      />
      <RadioModal
        testID="bp-plans-for-growth-modal"
        isVisible={isPlansForGrowthModalVisible}
        onSelectOption={(val) => {
          setPlansForGrowth(val);
          if (val === 'No') {
            setGrowthUnitsPerQuarter('');
            setGrowthOwnershipType('');
          }
        }}
        onButtonClickLeft={() => setPlansForGrowthModalVisible(false)}
        onClose={() => setPlansForGrowthModalVisible(false)}
        title="Any plans for growth or reduction of units?"
        options={yesNoOptions}
        selectedValue={plansForGrowth}
      />

      {/* Q7 + Q8 — conditional on plansForGrowth = Yes */}
      {plansForGrowth === 'Yes' && (
        <>
          <InputField
            variant="caption"
            label="How many units per quarter?"
            value={growthUnitsPerQuarter}
            onChange={(val) => setGrowthUnitsPerQuarter(val.replace(/[^0-9]/g, ''))}
            placeholder="Enter number of units"
            testID="bp-growth-units-per-quarter"
          />

          <IconInput
            label="Company owned or leased O/O?"
            testID="bp-growth-ownership-type"
            value={growthOwnershipType}
            placeholder="Select an option"
            onPress={() => setGrowthOwnershipTypeModalVisible(true)}
            iconSource={arrowDown}
            iconHeight={6.67}
            iconWidth={16.67}
          />
          <RadioModal
            testID="bp-growth-ownership-type-modal"
            isVisible={isGrowthOwnershipTypeModalVisible}
            onSelectOption={setGrowthOwnershipType}
            onButtonClickLeft={() => setGrowthOwnershipTypeModalVisible(false)}
            onClose={() => setGrowthOwnershipTypeModalVisible(false)}
            title="Company owned or leased O/O?"
            options={ownershipTypeOptions}
            selectedValue={growthOwnershipType}
          />
        </>
      )}

      {/* Q9 */}
      <IconInput
        label="Any plans to move the business in the next 12 months?"
        testID="bp-plans-to-relocate"
        value={plansToRelocate}
        placeholder="Select an option"
        onPress={() => setPlansToRelocateModalVisible(true)}
        iconSource={arrowDown}
        iconHeight={6.67}
        iconWidth={16.67}
      />
      <RadioModal
        testID="bp-plans-to-relocate-modal"
        isVisible={isPlansToRelocateModalVisible}
        onSelectOption={setPlansToRelocate}
        onButtonClickLeft={() => setPlansToRelocateModalVisible(false)}
        onClose={() => setPlansToRelocateModalVisible(false)}
        title="Any plans to move the business in the next 12 months?"
        options={yesNoOptions}
        selectedValue={plansToRelocate}
      />

      {saveErrorToast && (
        <Toast
          text="An error occurred while saving. Please try again."
          type="error"
          animate={true}
        />
      )}

      <NavigationButtons
        testID="bp-nav-btn"
        onBack={() => linkTo(URLS.APPLICATION.RISK_HISTORY)}
        onNext={handleNext}
        isNextDisabled={false}
      />
    </ScrollView>
  );
};

export default GrowthAndOwnershipScreen;
